import * as THREE from 'three';
import type { Controls } from '../core/device';
import type { Player } from '../entities/Player';
import type { MiniGame, MiniInput } from '../minigames/types';
import type { Vec2 } from '../world/layout';
import type { Stage, StageButton, StageHud, StageInput } from './types';

export interface ScreenSetup {
  /** The screen in the world (a plane whose UVs cover it), e.g. the poker machine's. */
  screen: THREE.Mesh;
  /** Where you stand, and which way you face. */
  stand: Vec2;
  facing: number;
  /** How far back from the screen the camera sits, and how high relative to the screen. */
  distance?: number;
  rise?: number;
  /** Touch buttons for this machine. */
  buttons?: StageButton[];
}

/**
 * A mini-game that plays on a screen in the world: the video poker machine, the TV.
 * The game draws itself onto a canvas that becomes the screen's texture, the camera
 * leans in close, and clicks on the screen land on the game where you clicked.
 */
export class ScreenStage implements Stage {
  readonly crosshair = false;
  readonly pointerLock = false;
  private readonly canvas: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D | null;
  private readonly tex: THREE.CanvasTexture;
  private readonly oldMat: THREE.Material | THREE.Material[];
  private mat: THREE.MeshStandardMaterial | null = null;
  private readonly ray = new THREE.Raycaster();
  private readonly center = new THREE.Vector3();
  private readonly normal = new THREE.Vector3();
  private t = 0;
  private camera: THREE.PerspectiveCamera | null = null;
  private baseFov = 60;

  constructor(
    readonly game: MiniGame,
    private readonly setup: ScreenSetup,
    private readonly player: Player,
  ) {
    const ratio = 2;
    this.canvas = document.createElement('canvas');
    this.canvas.width = game.width * ratio;
    this.canvas.height = game.height * ratio;
    this.g = this.canvas.getContext('2d');
    this.g?.scale(ratio, ratio);
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = 4;
    this.oldMat = setup.screen.material;
  }

  get title(): string {
    return this.game.title;
  }

  get done(): boolean {
    return this.game.done;
  }

  hint(controls: Controls): string {
    return this.game.hint(controls);
  }

  buttons(touch: boolean): StageButton[] {
    return touch ? (this.setup.buttons ?? []) : [];
  }

  begin(): void {
    const { player, setup } = this;
    player.teleport(setup.stand.x, setup.stand.z, setup.facing);
    player.scripted = true;
    player.viewHidden = true;
    player.anim.update({ dt: 1, time: 0, speed: 0, pose: 'stand' });
    this.mat = new THREE.MeshStandardMaterial({
      map: this.tex,
      emissive: new THREE.Color('#ffffff'),
      emissiveMap: this.tex,
      emissiveIntensity: 0.85,
      roughness: 0.3,
    });
    setup.screen.material = this.mat;
    this.draw();
  }

  /** Where on the game's canvas a click on the 3D view landed, if it hit the screen. */
  private canvasPoint(tap: { x: number; y: number }): { x: number; y: number } | null {
    if (!this.camera) return null;
    this.ray.setFromCamera(new THREE.Vector2(tap.x, tap.y), this.camera);
    const hit = this.ray.intersectObject(this.setup.screen, false)[0];
    if (!hit?.uv) return null;
    return { x: hit.uv.x * this.game.width, y: (1 - hit.uv.y) * this.game.height };
  }

  update(dt: number, input: StageInput): void {
    this.t += dt;
    const tap = input.tap ? this.canvasPoint(input.tap) : null;
    const mini: MiniInput = {
      held: input.held,
      pressed: input.pressed,
      released: input.released,
      tap,
      keys: input.keys,
    };
    this.game.update(dt, mini);
    this.player.anim.update({ dt, time: this.t, speed: 0, pose: 'stand' });
    this.draw();
  }

  private draw(): void {
    if (!this.g) return;
    this.game.draw(this.g, this.t);
    this.tex.needsUpdate = true;
  }

  frame(camera: THREE.PerspectiveCamera): void {
    if (this.camera !== camera) {
      this.camera = camera;
      this.baseFov = camera.fov;
    }
    const s = this.setup.screen;
    s.getWorldPosition(this.center);
    // The plane faces its local +z.
    this.normal.set(0, 0, 1).transformDirection(s.matrixWorld);
    const d = this.setup.distance ?? 0.95;
    camera.position.copy(this.center).addScaledVector(this.normal, d);
    camera.position.y += this.setup.rise ?? 0.04;
    camera.lookAt(this.center);
    // Fit the screen comfortably on any aspect ratio.
    const h = (this.game.height / this.game.width) * (s.geometry as THREE.PlaneGeometry).parameters.width;
    const w = (s.geometry as THREE.PlaneGeometry).parameters.width;
    const vfov = 2 * Math.atan((h * 0.95) / d);
    const hfov = 2 * Math.atan((w * 0.8) / d);
    const fov = THREE.MathUtils.radToDeg(Math.max(vfov, 2 * Math.atan(Math.tan(hfov / 2) / camera.aspect)));
    if (Math.abs(camera.fov - fov) > 0.01) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
  }

  hud(): StageHud {
    return {};
  }

  end(): void {
    if (this.camera) {
      this.camera.fov = this.baseFov;
      this.camera.updateProjectionMatrix();
    }
    this.setup.screen.material = this.oldMat;
    this.mat?.dispose();
    this.tex.dispose();
    this.player.scripted = false;
    this.player.viewHidden = false;
    this.player.syncTransform();
  }
}
